import React, { useEffect, useRef, useState } from 'react'
import { AuthLayout } from './AuthLayout';
import { Button } from '../../Common/Button';
import { Input } from '../../Common/Input';
import { Api } from '../../Service/api';
import { Await } from 'react-router-dom';
import Loading from '../../Common/Loading';
import { ErrorToast, SuccessToast } from '../../Common/Toast';
import { useDispatch, useSelector } from 'react-redux';
import { ACTIONS } from '../../redux/actions';
import { setlocalStorage, setToken } from '../../Service/api/configuration/localstorage';
import { path } from '../../Utils/ScreenPath';
import { getAppCheck } from '../../Service/appCheck';

const RESEND_SECONDS = 60;

const LoginForm = () => {
    const dispatch = useDispatch();
    const loginDetails = useSelector(state => state?.Login?.LoginData);
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState(false)
    const [loginData, setLoginData] = useState({
        mobileNumber: '',
        otp: ''
    })
    const [otpSent, setOtpSent] = useState(false)
    const [isSendingOtp, setIsSendingOtp] = useState(false)
    const [sentTo, setSentTo] = useState('') // number the last OTP went to
    const [cooldown, setCooldown] = useState(0) // seconds until the OTP can be resent
    const sendingRef = useRef(false) // blocks a double click before the disabled state renders

    // Start App Check early so the token is ready before the user clicks Send OTP
    useEffect(() => {
        getAppCheck()
    }, [])

    useEffect(() => {
        if (cooldown <= 0) return
        const timer = setTimeout(() => setCooldown((s) => s - 1), 1000)
        return () => clearTimeout(timer)
    }, [cooldown])

    const isResend = otpSent && loginData?.mobileNumber === sentTo
    const waitingToResend = isResend && cooldown > 0

    const validation = () => {
        let errors = {};
        let formIsValid = true;
        if (!loginData?.mobileNumber) {
            formIsValid = false;
            errors["mobileNumber"] = "Mobile Number cannot be empty";
        } else if (loginData?.mobileNumber?.length !== 10) {
            formIsValid = false;
            errors["mobileNumber"] = "Mobile Number must be 10 digits";
        }
        if (!otpSent) {
            formIsValid = false;
            errors["otp"] = "Please send OTP first";
        } else if (!loginData?.otp) {
            formIsValid = false;
            errors["otp"] = "OTP cannot be empty";
        }

        setError(errors);
        return formIsValid;
    }
    const handleChange = (name, value) => {
        const data = value.target.value
        if (name === "mobileNumber" && data.length > 10) return
        if (name === "otp" && data.length > 6) return
        setLoginData({ ...loginData, [name]: data })
    }

    const handleSubmit = async () => {
        if (validation()) {
            try {
                let payload = {
                    mobile: +loginData?.mobileNumber,
                    otp: +loginData?.otp
                }
                dispatch(ACTIONS.getLoginData(payload))
            } catch (e) {
                ErrorToast(e);
            } finally {
                // set loader false
            }
        }
    }

    useEffect(() => {
        if (loginDetails) {
            if (loginDetails?.status === 200) {
                if (loginDetails?.data?.isVerified) {
                    SuccessToast(loginDetails?.message);
                    setToken(loginDetails?.token)
                    setlocalStorage("userData", loginDetails?.data)
                    window.location.href = path?.SectionWise
                } else {
                    ErrorToast("User is not registered !");
                }
            } else {
                ErrorToast(loginDetails?.message);
            }
        }
    }, [loginDetails])

    const handleSendOtp = async () => {
        if (!loginData?.mobileNumber || loginData?.mobileNumber?.length !== 10) {
            setError({ mobileNumber: "Enter a valid 10 digit Mobile Number" });
            return;
        }
        if (sendingRef.current || waitingToResend) return;
        sendingRef.current = true;
        setError(false);
        setIsSendingOtp(true);
        try {
            let payload = {
                mobile: loginData?.mobileNumber
            }
            // Same number again → resend-otp; a new number → a fresh login OTP. No automatic retries.
            const { data } = isResend
                ? await Api.ResendOtp(JSON.stringify(payload))
                : await Api.SendOtp(JSON.stringify(payload))
            const res = JSON.parse(data);
            if (res?.status === 200 || res?.status === 429) {
                // 429 = rate-limited, but an earlier OTP may still be valid,
                // so let the user type it anyway.
                setOtpSent(true)
                setSentTo(loginData?.mobileNumber)
                setCooldown(RESEND_SECONDS)
                if (res?.status === 200) SuccessToast(res?.message);
                // no ErrorToast for 429 — ApiType.postRequest already shows it
            }
        } catch (e) {
            ErrorToast(e);
        } finally {
            sendingRef.current = false;
            setIsSendingOtp(false);
        }
    }

    const otpButtonText = isSendingOtp
        ? "Sending..."
        : waitingToResend
            ? `Resend OTP in ${cooldown}s`
            : isResend ? "Resend OTP" : "Send OTP"

    const onKeyDownHandler = e => {
        if (e.keyCode === 13 && !isLoading) {
            handleSubmit();
        }
    };

    return (
        <>
            <div className="flex flex-col items-center justify-center">
                <h2 className="mt-16 text-[#344767] text-3xl font-semibold text-center">
                    Log In
                </h2>
                <p className="text-[#7B809A] text-lg font-regular mt-4 font-roboto">
                    Enter your mobile number to Log In
                </p>
            </div>
            <div className="">
                <div className="mt-6">
                    <form onKeyDown={onKeyDownHandler} action="#" method="POST" className="space-y-4">
                        <Input
                            label="Mobile Number"
                            id="mobileNumber"
                            name="mobileNumber"
                            placeholder="Mobile Number"
                            type="number"
                            value={loginData?.mobileNumber}
                            onChange={(e) => handleChange("mobileNumber", e)}
                            handleSendOtp={handleSendOtp}
                            OTP={true}
                            otpButtonText={otpButtonText}
                            otpButtonDisabled={isSendingOtp || waitingToResend}
                            error={error?.mobileNumber ? true : false}
                            errorText={error?.mobileNumber}
                        />
                        <Input
                            label="OTP"
                            id="otp"
                            name="otp"
                            placeholder="Enter otp"
                            type="number"
                            value={loginData?.otp}
                            onChange={(e) => handleChange("otp", e)}
                            disabled={!otpSent}
                            error={error?.otp ? true : false}
                            errorText={error?.otp}
                        />
                        <div className="pt-1">
                            <Button
                                isLight
                                id=""
                                type="button"
                                label={isLoading ? "Loding..." : "Log In"}
                                onClick={() => {
                                    if (!isLoading) {
                                        handleSubmit();
                                    }
                                }}
                                extraClass="bg-gradient-to-b from-[#1190E4] to-[#3552D8] font-poppins"
                            />
                        </div>
                    </form>
                </div>
            </div>
        </>
    )
}

export default LoginForm